import React from 'react'
import { useDispatch } from 'react-redux'
import TextField from '../../elements/form/TextField';
import ModalActionButton from '../../elements/ModalActionButton';
import DateField from '../../elements/form/DateField';
import AutocompleteSelect from '../../elements/form/AutocompleteSelect';
import RadioGroup from '../../elements/form/RadioGroup';
import { Nationalities } from '../../../assets/static/nationalities';
import { authHeader, baseUrl, parseNigerianStates, slugify } from '../../../utils';
import axios from 'axios';
import { ERROR, SET_SUCCESS } from '../../../store/types';
import { StatesLgas } from '../../../assets/static/stateslgas';
import moment from 'moment';

const EditPersonalInformation = ({staff, closeFunction, reload}) => {
    const [staffPayload, setStaffPayload] = React.useState(staff || {})
    const [validationErrors, setValidationErrors] = React.useState({})

    const validateForm = () => {
        let errors = {}

        if(!staffPayload.firstName || staffPayload.firstName === '') {
            errors.firstName = true
        }

        if(!staffPayload.lastName || staffPayload.lastName === '') {
            errors.lastName = true
        }

        if(!staffPayload.email || staffPayload.email === '') {
            errors.email = true
        }

        if(!staffPayload.dob || staffPayload.dob === '') {
            errors.dateOfBirth = true
        }

        if(!staffPayload.maritalStatus || staffPayload.maritalStatus === '') {
            errors.maritalStatus = true
        }

        if(!staffPayload.bloodGroup || staffPayload.bloodGroup === '') {
            errors.bloodGroup = true
        }

        if(!staffPayload.origin.country || staffPayload.origin.country === '') {
            errors['origin-country'] = true
        }

        if(!staffPayload.origin.state || staffPayload.origin.state === '') {
            errors['origin-state'] = true
        }

        if(!staffPayload.origin.lga || staffPayload.origin.lga === '') {
            errors['origin-lga'] = true
        }

        if(!staffPayload.address || !staffPayload.address.address || staffPayload.address.address === '') {
            errors['address-address'] = true
        }

        if(!staffPayload.address || !staffPayload.address.city || staffPayload.address.city === '') {
            errors['address-city'] = true
        }

        if(!staffPayload.address || !staffPayload.address.state || staffPayload.address.state === '') {
            errors['address-state'] = true
        }

        if(!staffPayload.phone || staffPayload.phone === '') {
            errors.phone = true
        }

        setValidationErrors(errors)

        return errors
    }

    const [processing, setProcessing] = React.useState(false)

    const dispatch = useDispatch()

    const pushUpdate = async () => {
        if (Object.values(validateForm()).includes(true)) {
            return
        }
        const headers = { 'Content-Type': 'application/json', ...authHeader() }
        const payload = {...staffPayload}

        try {
            setProcessing(true) 
            await axios.patch(`${baseUrl}/user/profile/staff`, payload, { headers })
            setProcessing(false)
            dispatch({
                type: SET_SUCCESS,
                payload: "Personal information updated successfully"
            })
            reload()
            closeFunction()
        } catch (error) {
            console.error(error)
            dispatch({
                type: ERROR,
                error
            })
            setProcessing(false)    
        }
    }

    const updateAddress = (field, value) => {
        let temp = {...staffPayload}
        temp.address[field] = value
        setStaffPayload(temp)
    }

    const maritalStatusOptions = [
        {label: 'Single', value: 'single'},
        {label: 'Married', value: 'married'},
        {label: 'Divorced', value: 'divorced'},
        {label: 'Widowed', value: 'widowed'},
    ]

    const bloodGroupOptions = [
        {label: 'A+', value: 'A+'},
        {label: 'A-', value: 'A-'},
        {label: 'B+', value: 'B+'},
        {label: 'B-', value: 'B-'},
        {label: 'AB+', value: 'AB+'},
        {label: 'AB-', value: 'AB-'},
        {label: 'O+', value: 'O+'},
        {label: 'O-', value: 'O-'},
    ]

    const [activeLgas, setActiveLgas] = React.useState([])
    const [lgasEnabled, setLgasEnabled] = React.useState(false)

    const selectState = (selectedState) => {
        setLgasEnabled(false)
        const selectedStateLgas = StatesLgas.find((state)=>{
            return state.stateSlug === slugify(selectedState.label)
        })?.lgas

        setActiveLgas(selectedStateLgas)

        setTimeout(() => {
            setLgasEnabled(true)
        }, 50);

        setStaffPayload({...staffPayload, ...{origin: {...staffPayload.origin, state: selectedState.label}}})
    }


    return (
        <div className="w-full">
            <div className="w-full flex items-start justify-between gap-x-3">
                <div className='mt-3 w-full'>
                    <TextField
                        inputLabel="First name" 
                        inputPlaceholder="Your given name" 
                        fieldId="first-name" 
                        inputType="first-name" 
                        disabled={false}
                        requiredField={true}
                        preloadValue={staffPayload.firstName || ''}
                        hasError={validationErrors.firstName} 
                        returnFieldValue={(value)=>{setStaffPayload({...staffPayload, ...{firstName: value}})}}
                    />
                </div>
                
                <div className='mt-3 w-full'>
                    <TextField
                        inputLabel="Other names" 
                        inputPlaceholder="Middle name" 
                        fieldId="middle-name" 
                        inputType="text" 
                        hasError={false} 
                        disabled={false}
                        preloadValue={staffPayload.otherNames || ''}
                        returnFieldValue={(value)=>{setStaffPayload({...staffPayload, ...{otherNames: value}})}}
                    />
                </div>
                
                <div className='mt-3 w-full'>
                    <TextField
                        inputLabel="Last name" 
                        inputPlaceholder="Your surname" 
                        fieldId="last-name" 
                        inputType="text" 
                        hasError={validationErrors.lastName} 
                        disabled={false}
                        requiredField={true}
                        preloadValue={staffPayload.lastName || ''}
                        returnFieldValue={(value)=>{setStaffPayload({...staffPayload, ...{lastName: value}})}}
                    />
                </div>
            </div>

            <div className="w-full flex items-start justify-between gap-x-3">
                <div className='mt-3 w-full'>
                    <DateField
                        inputLabel="Date of birth" 
                        inputPlaceholder="Your date of birth" 
                        fieldId="date-of-birth" 
                        inputType="text" 
                        hasError={validationErrors.dateOfBirth} 
                        disabled={false}
                        requiredField={true}
                        preloadValue={staffPayload.dob ? moment(staffPayload.dob).format('YYYY-MM-DD') : ''}
                        returnFieldValue={(value)=>{setStaffPayload({...staffPayload, ...{dob: new Date(value)}})}}
                    />
                </div>
                <div className='mt-3 w-full'>
                    <AutocompleteSelect
                        selectOptions={maritalStatusOptions} 
                        inputLabel="Marital status" 
                        placeholderText="Select your marital status"
                        displayImage={false} 
                        imageField=""
                        titleField="label" 
                        preSelected={staffPayload.maritalStatus || ''}
                        preSelectedLabel={'value'}
                        hasError={validationErrors.maritalStatus} 
                        returnFieldValue={(value)=>{setStaffPayload({...staffPayload, ...{maritalStatus: value.value}})}}
                        includeButton={false}
                        buttonLabel=""
                        buttonAction={()=>{}}
                        disabled={false}
                        requiredField={true}
                        conditionalItemStyling={null}
                    />
                </div>
            </div>

            <div className="w-full mt-3">
                <RadioGroup 
                    items={bloodGroupOptions} 
                    inline={true}
                    preSelectedIndex={bloodGroupOptions.findIndex(item => item.value === staffPayload.bloodGroup)}
                    inputLabel={'Blood Group'}
                    requiredField={true}
                    returnSelected={(value) => {setStaffPayload({...staffPayload, ...{bloodGroup: value.value}})}} 
                    hasError={validationErrors && validationErrors.bloodGroup}  
                    titleField='label'
                />
            </div>
            
            <h2 className="font-space-grotesk text-sm mt-5 font-semibold text-gray-900">
                Origin information
            </h2>

            <div className="w-full flex items-start justify-between gap-x-3">
                <div className='w-full mt-3'>
                    <AutocompleteSelect
                        selectOptions = {Nationalities}
                        requiredField={true}
                        inputLabel = "Nationality"
                        titleField = "name"
                        displayImage = {true}
                        placeholderText = "Select your nationality"
                        imageField = "image"
                        fieldId = "citizenship"
                        preSelected={staffPayload.origin?.country && staffPayload.origin?.country !== '' ? staffPayload.origin?.country : ''}
                        preSelectedLabel='name'
                        hasError = {validationErrors && validationErrors.country}
                        returnFieldValue={(value)=>{setStaffPayload({...staffPayload, ...{origin: {...staffPayload.origin, country: value.name, countryCode: value.code}}})}}
                        disabled={false}
                    />
                </div>

                <div className='w-full mt-3'>
                    {staffPayload.origin?.countryCode === 'NG' || staffPayload.origin?.country === 'Nigeria' ?
                        <AutocompleteSelect
                            selectOptions = {parseNigerianStates()}
                            inputLabel = "State of Origin"
                            requiredField={true}
                            titleField = "label"
                            placeholderText = "Select your state of origin"
                            displayImage = {false}
                            imageField = ""
                            fieldId = "State"
                            preSelected={staffPayload.origin?.state && staffPayload.origin?.state !== '' ? staffPayload.origin?.state : ''}
                            preSelectedLabel='label'
                            hasError = {validationErrors && validationErrors.state}
                            returnFieldValue={(value)=>{selectState(value)}}
                        />
                    :
                        <TextField 
                            inputType="text" 
                            fieldId="state"
                            requiredField={true}
                            inputLabel="State of Origin" 
                            inputPlaceholder="Your state of origin"
                            hasError={validationErrors && validationErrors[`origin-state`] === true} 
                            preloadValue={staffPayload.origin?.state || ''}
                            // disabled={true}
                            disabled={false}
                            returnFieldValue={(value)=>{setStaffPayload({...staffPayload, ...{origin: {...staffPayload.origin, state: value.label}}})}}
                        />

                    }
                </div>

                <div className='w-full mt-3'>
                    {(staffPayload.origin?.countryCode === 'NG' || staffPayload.origin?.country === 'Nigeria') && lgasEnabled === true ?
                        <AutocompleteSelect
                            selectOptions = {activeLgas}
                            inputLabel = "LGA/City"
                            titleField = ""
                            displayImage = {false}
                            imageField = ""
                            placeholderText = "Select your LGA/City"
                            disabled={!lgasEnabled}
                            fieldId = "userLga"
                            preSelected={staffPayload.origin?.lga && staffPayload.origin?.lga !== '' ? staffPayload.origin?.lga : ''}
                            requiredField={true}
                            preSelectedLabel=''
                            hasError = {validationErrors && validationErrors['origin-lga'] === true}
                            returnFieldValue={(value)=>{
                                setStaffPayload({...staffPayload, ...{origin: {...staffPayload.origin, lga: value}}})}}
                        />
                    :
                        <TextField 
                            inputType="text" 
                            fieldId="city"
                            inputLabel="LGA/City" 
                            inputPlaceholder="Your LGA/City"
                            hasError={validationErrors && validationErrors['origin-lga'] === true} 
                            preloadValue={staffPayload.origin?.lga || ''}
                            // disabled={true}
                            requiredField={true}
                            disabled={false}
                            returnFieldValue={(value)=>{setStaffPayload({...staffPayload, ...{origin: {...staffPayload.origin, lga: value}}})}}
                        />
                    }
                </div>
            </div>

            <h2 className="font-space-grotesk text-sm mt-5 font-semibold text-gray-900">
                Contact information
            </h2>

            <div className="w-full flex items-start justify-between gap-x-3">
                <div className='mt-3 w-full'>
                    <TextField
                        inputLabel="Email" 
                        inputPlaceholder="Your email address" 
                        fieldId="email" 
                        inputType="email" 
                        hasError={validationErrors.email} 
                        disabled={true}
                        requiredField={true}
                        preloadValue={staffPayload.email || ''}
                        returnFieldValue={(value)=>{setStaffPayload({...staffPayload, ...{email: value}})}}
                    />
                </div>

                <div className='mt-3 w-full'>
                    <TextField
                        inputLabel="Phone number" 
                        inputPlaceholder="Your phone number" 
                        fieldId="phone" 
                        inputType="text" 
                        hasError={validationErrors.phone} 
                        disabled={false}
                        requiredField={true}
                        preloadValue={staffPayload.phone || ''}
                        returnFieldValue={(value)=>{setStaffPayload({...staffPayload, ...{phone: value}})}}
                    />
                </div>

                <div className='mt-3 w-full'>
                    <TextField
                        inputLabel="Alternate phone number" 
                        inputPlaceholder="Your alternate phone number" 
                        fieldId="alt-phone" 
                        inputType="text" 
                        hasError={validationErrors.altPhone} 
                        disabled={false}
                        requiredField={false}
                        preloadValue={staffPayload.altPhone || ''}
                        returnFieldValue={(value)=>{setStaffPayload({...staffPayload, ...{altPhone: value}})}}
                    />
                </div>
            </div>

            <div className='mt-3 w-full'>
                <TextField
                    inputLabel="Address" 
                    fieldId="employee-address" 
                    inputType="text" 
                    requiredField={true}
                    preloadValue={staffPayload.address?.address || ''}
                    hasError={validationErrors['address-address']} 
                    inputPlaceholder={`Employee physical address`}
                    returnFieldValue={(value)=>{updateAddress('address', value)}}
                />
            </div>
            <div className='w-full flex items-start justify-between gap-x-3'>
                <div className='mt-3 w-full'>
                    <TextField
                        inputLabel="City" 
                        fieldId="employee-address-city" 
                        inputType="text" 
                        requiredField={true}
                        preloadValue={staffPayload.address?.city || ''}
                        hasError={validationErrors['address-city']} 
                        inputPlaceholder={`City or LGA (eg: Makurdi)`}
                        returnFieldValue={(value)=>{updateAddress('city', value)}}
                    />
                </div>
                <div className='mt-3 w-full'>
                    <TextField
                        inputLabel="State" 
                        fieldId="employee-address-state" 
                        inputType="text" 
                        requiredField={true}
                        preloadValue={staffPayload.address?.state || ''}
                        hasError={validationErrors['address-state']} 
                        inputPlaceholder={`Address state (eg: Benue)`}
                        returnFieldValue={(value)=>{updateAddress('state', value)}}
                    />
                </div>
            </div>

            <div className="mt-8 flex flex-row-reverse gap-x-4">
                <ModalActionButton
                    buttonLabel={`Update personal information`}
                    buttonAction={()=>{pushUpdate()}}
                    processing={processing}
                />

                <button
                    type="button"
                    className="inline-flex font-space-grotesk justify-center px-5 py-4 text-xs font-medium bg-transparent border border-transparent rounded-md hover:bg-opacity-50 hover:bg-secondary/40 focus:outline-none"
                    onClick={closeFunction}
                >
                    Close
                </button>
                
            </div>
        </div>
    )
}

export default EditPersonalInformation